import 'core-js/stable'
import 'regenerator-runtime/runtime'
import 'src/utils/userpilotHelpers'

import axe from '@axe-core/react'
import { datadogLogs } from '@datadog/browser-logs'
import { datadogRum } from '@datadog/browser-rum'
import {
  createTheme,
  ThemeProvider as MuiThemeProvider,
} from '@mui/material/styles'
import { LicenseInfo } from '@mui/x-license'
import { LocaleContextProvider } from '@paywithextend/components'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { ConnectedRouter } from 'connected-react-router'
import cssVars from 'css-vars-ponyfill'
import { ResolvedColors } from 'ext-ui/style/themes'
import { createBrowserHistory } from 'history'
import React from 'react'
import { CookiesProvider } from 'react-cookie'
import ReactDOM from 'react-dom'
import { createRoot } from 'react-dom/client'
import Modal from 'react-modal'
import { Provider } from 'react-redux'
import { DeepPartial } from 'redux'
import { composeWithDevTools } from 'redux-devtools-extension'
import { AnalyticsProvider } from 'src/analytics/AnalyticsProvider'
import App from 'src/app/App'
import { Environments } from 'src/configurations'
import { AgentProvider } from 'src/context/AgentContext'
import ConfigContextProvider from 'src/context/ConfigContext/ConfigContext'
import { GlobalModalProvider } from 'src/context/GlobalModalContext/GlobalModalContext'
import UserProvider from 'src/context/UserProvider/UserProvider'
import { queryClient } from 'src/queryClient'
import configureStore from 'src/redux/configureStore'
import { LocationState, RootState } from 'src/redux/createRootReducer'
import { initialState as initialAppConfigState } from 'src/redux/reducers/data/config.reducer'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import GlobalStyles from 'src/style/globalStyles'
import { UserPilotHelpers, userPilotHelpers } from 'src/utils/userpilotHelpers'
import { ThemeProvider } from 'styled-components'
import { Userpilot } from 'userpilot'

import { ExtendLocales } from './enums'

// types
declare global {
  interface Window {
    __REQUEST_COUNTRY__: string
    __REQUEST_URL__: string
    __NONCE__: string
    __INITIAL_STATE__: DeepPartial<RootState>
    __REDUX_DEVTOOLS_EXTENSION_COMPOSE__?: typeof composeWithDevTools
    __EXT_UI_COLORS__?: ResolvedColors

    userpilot?: unknown
    __UP_HELPERS__?: UserPilotHelpers
    initAddToAppleWallet?: any
  }
}

// history
const history = createBrowserHistory<LocationState>()

// data
const initialState = window.__INITIAL_STATE__
const envConfig = JSON.parse(
  document.getElementById('__APP_CONFIG__')?.innerHTML || '{}',
)
const brandConfig = JSON.parse(
  document.getElementById('__BRAND_CONFIG__')?.innerHTML || '{}',
)

export const store = configureStore(
  { ...initialState, session: { ...initialState.session } },
  history,
  envConfig,
  true,
)

// datadog
if (envConfig.DATADOG_APP_ID && envConfig.DATADOG_CLIENT_TOKEN) {
  if (
    envConfig.ENVIRONMENT !== Environments.Dev &&
    envConfig.ENVIRONMENT !== Environments.Test
  ) {
    const datadogSite = 'datadoghq.com'
    const datadogService = 'extend-app'

    datadogRum.init({
      applicationId: envConfig.DATADOG_APP_ID,
      clientToken: envConfig.DATADOG_CLIENT_TOKEN,
      site: datadogSite,
      service: datadogService,
      env: envConfig.ENVIRONMENT,
      sessionSampleRate: 100,
      sessionReplaySampleRate:
        envConfig.ENVIRONMENT === Environments.Prod ? 30 : 0,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'mask',
      silentMultipleInit: true,
      version: envConfig.VERSION,
    })

    datadogLogs.init({
      clientToken: envConfig.DATADOG_CLIENT_TOKEN,
      site: datadogSite,
      service: datadogService,
      env: envConfig.ENVIRONMENT,
      forwardErrorsToLogs: true,
      sessionSampleRate: 100,
      silentMultipleInit: true,
    })
  }
}

// react-modal
Modal.setAppElement('#content')

if (envConfig.USERPILOT_APP_TOKEN) {
  window.__UP_HELPERS__ = userPilotHelpers
  const userpilotSettings = {
    token: envConfig.USERPILOT_APP_TOKEN,
    nonce: window.__NONCE__,
    endpoint: 'up.paywithextend.com',
    domain: 'upsdk.paywithextend.com',
    proxy: 'all',
  }
  Userpilot.initialize(envConfig.USERPILOT_APP_TOKEN, userpilotSettings)
}

const axeConf = {
  runOnly: ['wcag2a', 'wcag2aa'],
}

if (envConfig.ENVIRONMENT === Environments.Dev) {
  axe(React, ReactDOM, 1000, axeConf)
}

// css variables
cssVars({
  onBeforeSend(xhr, elm, url) {
    xhr.open('GET', `${url}?xhr=true`)
  },
  variables: {
    '--color-highlight': brandConfig.COLOR_HIGHLIGHT,
    '--color-highlight-dark': brandConfig.COLOR_HIGHLIGHT_DARK,
    '--color-cta': brandConfig.COLOR_CTA,
    '--color-cta-dark': brandConfig.COLOR_CTA_DARK,
    '--color-font-primary': brandConfig.COLOR_FONT_PRIMARY,
  },
})

const render = (): void => {
  const theme = createTheme({
    typography: {
      fontFamily: 'unset',
    },
  })

  LicenseInfo.setLicenseKey(envConfig.MUI_LICENSE_KEY || '')

  // Locale is put here so that global modals have access to translations
  const locale = selectNullSafeLocaleFromState(store.getState())

  const html = (
    <ThemeProvider
      theme={{
        colors: {
          highlight: brandConfig.COLOR_HIGHLIGHT,
          highlight_dark: brandConfig.COLOR_HIGHLIGHT_DARK,
          cta: brandConfig.COLOR_CTA,
          cta_dark: brandConfig.COLOR_CTA_DARK,
          font_primary: brandConfig.COLOR_FONT_PRIMARY,
        },
      }}
    >
      <GlobalStyles />
      <CookiesProvider>
        <Provider store={store}>
          <AnalyticsProvider>
            <ConnectedRouter history={history}>
              <QueryClientProvider client={queryClient}>
                <ConfigContextProvider
                  initialState={{
                    brand: brandConfig,
                    environment: envConfig,
                    locale: (window?.navigator?.language ||
                      'en-US') as ExtendLocales,
                    appConfig: initialAppConfigState,
                  }}
                >
                  <UserProvider>
                    <LocaleContextProvider locale={locale}>
                      <GlobalModalProvider>
                        <AgentProvider>
                          <ReactQueryDevtools buttonPosition={'bottom-left'} />
                          <MuiThemeProvider theme={theme}>
                            <App />
                          </MuiThemeProvider>
                        </AgentProvider>
                      </GlobalModalProvider>
                    </LocaleContextProvider>
                  </UserProvider>
                </ConfigContextProvider>
              </QueryClientProvider>
            </ConnectedRouter>
          </AnalyticsProvider>
        </Provider>
      </CookiesProvider>
    </ThemeProvider>
  )

  const root = createRoot(document.getElementById('content') ?? document.body)
  root.render(html)
}

render()
